import type {AGUIEvent, Message, State} from '@ag-ui/core';
import type {ChatStatusItem, ChatStatusItemEvent} from './chat-status';
import {
    chatStatusItemEventRemovesItem,
    chatStatusItemFromEvent,
    chatStatusItemIdFromEvent,
    chatStatusItemsEqual,
} from './chat-status';

export type ChatAgUiEvent = AGUIEvent;

export type ChatMessage = {
    id: string;
    role: string;
    content: string;
    toolCalls: ChatToolCall[];
    toolCallId?: string;
    createdAt: number;
    updatedAt: number;
    durationMs?: number;
};

export type ChatToolCallStatus = 'running' | 'done' | 'error';

export type ChatToolCall = {
    id: string;
    name: string;
    args: string;
    result?: string;
    status: ChatToolCallStatus;
};

export type ChatSnapshot = {
    messages: ChatMessage[];
    statusItems: ChatStatusItem[];
    state: State;
    events: ChatAgUiEvent[];
    running: boolean;
    error: string | null;
    errorCode: string | null;
};

type ChatStateChangeBase = {
    statusItems: ChatStatusItem[];
    running: boolean;
    error: string | null;
    errorCode: string | null;
};

export type ChatAgUiStateChange = ChatStateChangeBase & {
    event: ChatAgUiEvent;
    messages: ChatMessage[];
};

export type ChatStatusStateChange = ChatStateChangeBase;

type StateListener = (state: State) => void;

export class ChatStateStore {
    private messages = new Map<string, ChatMessage>();
    private statusItems = new Map<string, ChatStatusItem>();
    private order: string[] = [];
    private toolCallMessageIds = new Map<string, string>();
    private toolCallResults = new Map<string, string>();
    private state: State = {};
    private events: ChatAgUiEvent[] = [];
    private running = false;
    private error: string | null = null;
    private errorCode: string | null = null;
    private stateListeners = new Set<StateListener>();

    constructor(events: ChatAgUiEvent[] = []) {
        events.forEach(event => this.apply(event));
    }

    apply(event: ChatAgUiEvent, record = true): ChatAgUiStateChange | null {
        if (record) {
            this.events.push(event);
        }
        const now = Date.now();
        const changedMessageIds = new Set<string>();
        let changed = false;
        const track = (message: ChatMessage | null | undefined): void => {
            if (message) {
                changedMessageIds.add(message.id);
            }
        };

        switch (event.type) {
            case 'RUN_STARTED':
                this.running = true;
                this.error = null;
                this.errorCode = null;
                event.input?.messages?.forEach(message => track(this.upsertMessage(message, now)));
                changed = true;
                break;

            case 'RUN_FINISHED':
                this.running = false;
                // Reasoning duration is bounded by reasoning events, not by the
                // whole run. Tool calls or answer streaming can continue long
                // after the thought has ended.
                this.markRunningToolCalls('done').forEach(id => changedMessageIds.add(id));
                changed = true;
                break;

            case 'RUN_ERROR':
                this.running = false;
                this.error = event.message || 'Run failed';
                this.errorCode = typeof event.code === 'string' ? event.code : null;
                // Keep the last reasoning timestamp at its final reasoning
                // event; otherwise timeout/error latency becomes "thought" time.
                this.markRunningToolCalls('error').forEach(id => changedMessageIds.add(id));
                changed = true;
                break;

            case 'TEXT_MESSAGE_START':
                track(this.ensureMessage(event.messageId, event.role || 'assistant', now));
                changed = true;
                break;

            case 'TEXT_MESSAGE_CONTENT':
                track(this.appendContent(event.messageId, event.delta || '', 'assistant', now));
                changed = true;
                break;

            case 'REASONING_START':
                track(this.ensureMessage(event.messageId, 'reasoning', now));
                changed = true;
                break;

            case 'REASONING_MESSAGE_START':
                track(this.ensureMessage(event.messageId, event.role || 'reasoning', now));
                changed = true;
                break;

            case 'REASONING_MESSAGE_CONTENT':
                track(this.appendContent(event.messageId, event.delta || '', 'reasoning', now));
                changed = true;
                break;

            case 'REASONING_MESSAGE_END':
                track(this.finishReasoningMessage(event.messageId, now));
                changed = true;
                break;

            case 'REASONING_END':
                track(this.finishReasoningMessage(event.messageId, now));
                changed = true;
                break;

            case 'TOOL_CALL_START':
                track(this.addToolCall(event.parentMessageId || event.toolCallId, event.toolCallId, event.toolCallName, now));
                changed = true;
                break;

            case 'TOOL_CALL_ARGS':
                track(this.appendToolArgs(event.toolCallId, event.delta || '', now));
                changed = true;
                break;

            case 'TOOL_CALL_END':
                track(this.touchToolCallMessage(event.toolCallId, now));
                changed = true;
                break;

            case 'TOOL_CALL_RESULT':
                track(this.setToolResult(event.toolCallId, event.content || '', now));
                this.upsertMessage({
                    id: event.messageId,
                    role: event.role || 'tool',
                    content: event.content,
                    toolCallId: event.toolCallId,
                } as Message, now);
                changed = true;
                break;

            case 'STATE_SNAPSHOT':
                this.state = event.snapshot || {};
                break;
        }

        if (!changed) {
            return null;
        }

        return {
            event,
            messages: Array.from(changedMessageIds)
                .map(id => this.messages.get(id))
                .filter((message): message is ChatMessage => message !== undefined),
            statusItems: this.getStatusItems(),
            running: this.running,
            error: this.error,
            errorCode: this.errorCode,
        };
    }

    applyStatusItemEvent(event: ChatStatusItemEvent): ChatStatusStateChange | null {
        const changed = this.trackStatusItemEvent(event, Date.now());
        if (!changed) {
            return null;
        }

        return {
            statusItems: this.getStatusItems(),
            running: this.running,
            error: this.error,
            errorCode: this.errorCode,
        };
    }

    subscribeState(listener: StateListener): () => void {
        this.stateListeners.add(listener);
        listener(this.state);

        return () => this.stateListeners.delete(listener);
    }

    setMessages(messages: readonly Message[]): void {
        this.messages.clear();
        this.order = [];
        this.toolCallMessageIds.clear();
        messages.forEach(message => this.upsertMessage(message));
    }

    setState(state: State): void {
        this.state = state || {};
        this.stateListeners.forEach(listener => listener(this.state));
    }

    snapshot(): ChatSnapshot {
        return {
            messages: this.order
                .map(id => this.messages.get(id))
                .filter((message): message is ChatMessage => message !== undefined),
            statusItems: this.getStatusItems(),
            state: this.state,
            events: [...this.events],
            running: this.running,
            error: this.error,
            errorCode: this.errorCode,
        };
    }

    eventCount(): number {
        return this.events.length;
    }

    private upsertMessage(message: Message, now = Date.now()): ChatMessage | null {
        if (!message.id || !message.role) {
            return null;
        }

        const existing = this.messages.get(message.id);
        if (!existing) {
            this.order.push(message.id);
        }

        const next: ChatMessage = {
            id: message.id,
            role: message.role,
            content: typeof message.content === 'string' ? message.content : '',
            toolCalls: existing?.toolCalls || [],
            toolCallId: 'toolCallId' in message && typeof message.toolCallId === 'string' ? message.toolCallId : undefined,
            createdAt: existing?.createdAt || now,
            updatedAt: now,
            durationMs: existing?.durationMs,
        };

        if ('toolCalls' in message && Array.isArray(message.toolCalls)) {
            next.toolCalls = message.toolCalls.map(toolCall => ({
                id: toolCall.id,
                name: toolCall.function.name,
                args: toolCall.function.arguments,
                result: this.toolCallResults.get(toolCall.id),
                status: this.toolCallResults.has(toolCall.id) ? 'done' : existing?.toolCalls
                    .find(existingToolCall => existingToolCall.id === toolCall.id)?.status || 'running',
            }));
            next.toolCalls.forEach(toolCall => this.toolCallMessageIds.set(toolCall.id, message.id));
        }

        this.messages.set(message.id, next);
        return next;
    }

    private trackStatusItemEvent(event: ChatStatusItemEvent, now: number): boolean {
        const id = chatStatusItemIdFromEvent(event);
        if (id === '') {
            return false;
        }

        if (chatStatusItemEventRemovesItem(event)) {
            return this.statusItems.delete(id);
        }

        const item = chatStatusItemFromEvent(event, now);
        if (item === null) {
            return false;
        }

        const existing = this.statusItems.get(item.id);
        if (existing !== undefined && chatStatusItemsEqual(existing, item)) {
            return false;
        }

        this.statusItems.set(item.id, item);
        return true;
    }

    private getStatusItems(): ChatStatusItem[] {
        return Array.from(this.statusItems.values())
            .sort((left, right) => left.updatedAt - right.updatedAt);
    }

    private ensureMessage(id: string, role: string, now = Date.now()): ChatMessage {
        const existing = this.messages.get(id);
        if (existing) {
            return existing;
        }

        const message: ChatMessage = {id, role, content: '', toolCalls: [], createdAt: now, updatedAt: now};
        this.messages.set(id, message);
        this.order.push(id);
        return message;
    }

    private appendContent(id: string, delta: string, role: string, now: number): ChatMessage {
        const message = this.ensureMessage(id, role, now);
        message.content += delta;
        message.updatedAt = now;
        return message;
    }

    private addToolCall(messageId: string, toolCallId: string, toolCallName: string, now: number): ChatMessage {
        const message = this.ensureMessage(messageId, 'assistant', now);
        message.updatedAt = now;
        if (message.toolCalls.some(toolCall => toolCall.id === toolCallId)) {
            return message;
        }

        message.toolCalls.push({
            id: toolCallId,
            name: toolCallName,
            args: '',
            result: this.toolCallResults.get(toolCallId),
            status: this.toolCallResults.has(toolCallId) ? 'done' : 'running',
        });
        this.toolCallMessageIds.set(toolCallId, messageId);
        return message;
    }

    private appendToolArgs(toolCallId: string, delta: string, now: number): ChatMessage | null {
        const toolCall = this.findToolCall(toolCallId);
        if (toolCall) {
            toolCall.args += delta;
            return this.touchToolCallMessage(toolCallId, now);
        }

        return null;
    }

    private setToolResult(toolCallId: string, result: string, now: number): ChatMessage | null {
        this.toolCallResults.set(toolCallId, result);
        const toolCall = this.findToolCall(toolCallId);
        if (toolCall) {
            toolCall.result = result;
            toolCall.status = toolResultHasError(result) ? 'error' : 'done';
            return this.touchToolCallMessage(toolCallId, now);
        }

        return null;
    }

    private findToolCall(toolCallId: string): ChatToolCall | null {
        const messageId = this.toolCallMessageIds.get(toolCallId);
        const message = messageId ? this.messages.get(messageId) : null;
        return message?.toolCalls.find(toolCall => toolCall.id === toolCallId) || null;
    }

    private touchToolCallMessage(toolCallId: string, now: number): ChatMessage | null {
        const messageId = this.toolCallMessageIds.get(toolCallId);
        if (messageId) {
            return this.touchMessage(messageId, now);
        }

        return null;
    }

    private touchMessage(messageId: string, now: number): ChatMessage | null {
        const message = this.messages.get(messageId);
        if (message) {
            message.updatedAt = now;
        }
        return message || null;
    }

    private finishReasoningMessage(messageId: string, now: number): ChatMessage | null {
        const message = this.messages.get(messageId);
        if (!message || message.role !== 'reasoning') {
            return null;
        }

        // Store the final thinking duration separately. The message timestamp
        // can still move for non-reasoning updates, but collapsed summaries
        // should describe only the reasoning start/end span.
        message.durationMs = Math.max(0, now - message.createdAt);
        message.updatedAt = message.createdAt + message.durationMs;
        return message;
    }

    private markRunningToolCalls(status: ChatToolCallStatus): string[] {
        const changedMessageIds: string[] = [];
        this.messages.forEach(message => {
            let changed = false;
            message.toolCalls.forEach(toolCall => {
                if (toolCall.status === 'running') {
                    toolCall.status = status;
                    changed = true;
                }
            });
            if (changed) {
                changedMessageIds.push(message.id);
            }
        });
        return changedMessageIds;
    }
}

function toolResultHasError(result: string): boolean {
    if (!result) {
        return false;
    }

    try {
        const parsed = JSON.parse(result);
        return typeof parsed === 'object' && parsed !== null && 'ok' in parsed && parsed.ok === false;
    } catch {
        return false;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
